import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlteGrundbuchmeldungListEntry } from '../../../core/gb-api';
import { ListLoader } from '../../../shared/components/list-loader/list-loader';
import { ListLoaderPreloaded } from '../../../shared/components/list-loader/list-loader-preloaded';
import { TableSortModel } from '../../../shared/tablesort/tableSortModel';
import { ComparatorService } from '../../../shared/services/comparator-service';
import { DokulinkItemBuilderFactory } from '../../../shared/components/dokulink/dokulinkItemBuilderFactory';
import { DokulinkItem } from '../../../shared/components/dokulink/dokulinkItem';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { ListLoaderWrapperComponent } from '../../../shared/components/list-loader-wrapper/list-loader-wrapper.component';
import { NgTemplateOutlet } from '@angular/common';
import { CapSpinnerSmallComponent } from '../../../shared/components/cap-spinner-small/cap-spinner-small.component';
import { ColumnHeaderComponent } from '../../../shared/tablesort/sort-icon/column-header.component';
import { DokulinkComponent } from '../../../shared/components/dokulink/dokulink.component';
import { TranslatePipe } from '@ngx-translate/core';
import { DatumPipe } from '../../../shared/pipes/simple/datum.pipe';
import { FormatGeschaeftsnummerPipe } from '../../../shared/pipes/format/format-geschaeftsnummer.pipe';
import { GrundstuecknummerPipe } from '../../../shared/pipes/simple/grundstuecknummer.pipe';

@Component({
  templateUrl: './alte-grundbuchmeldung-list.component.html',
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    ListLoaderWrapperComponent,
    NgTemplateOutlet,
    CapSpinnerSmallComponent,
    ColumnHeaderComponent,
    DokulinkComponent,
    TranslatePipe,
    DatumPipe,
    FormatGeschaeftsnummerPipe,
    GrundstuecknummerPipe
  ]
})
export class AlteGrundbuchmeldungListComponent implements OnInit {
  listLoader: ListLoader<AlteGrundbuchmeldungListEntryVm>;
  tableSortModel = new TableSortModel<AlteGrundbuchmeldungListEntryVm>('table', this.comparatorService);

  constructor(
    private comparatorService: ComparatorService,
    private route: ActivatedRoute,
    private dokuLinkBuilderFactory: DokulinkItemBuilderFactory
  ) {
    keysToTranslate<string>(['MeldungsId']);
    this.tableSortModel
      .addColumn('geschaeftsNummer', (a, b) => this.orderByTagebuchnummerThenMeldungsIdThenGrundstueck(a.object, b.object))
      .switchSortDirection();
    this.tableSortModel.addColumn('meldungsId', (a, b) => a.object.meldungsId - b.object.meldungsId);
    this.tableSortModel.addColumn('abschlussdatum', (a, b) =>
      this.comparatorService.dateComparator(a.object.abschlussDatum, b.object.abschlussDatum)
    );
    this.tableSortModel.addColumn('beschreibung', (a, b) =>
      this.comparatorService.translatedStringComparator(a.object.beschreibung, b.object.beschreibung)
    );
    this.tableSortModel.addColumn('grundstueck', (a, b) => this.sortByGrundstueckNummer(a.object, b.object));
  }

  ngOnInit() {
    const viewModels = this.route.snapshot.data.entries.map((g) => new AlteGrundbuchmeldungListEntryVm(g, this.dokuLinkBuilderFactory));
    this.listLoader = new ListLoaderPreloaded(15, 1, viewModels);
    this.listLoader.applySortModel(this.tableSortModel);
  }

  open(url: string) {
    window.open(url);
  }

  private orderByTagebuchnummerThenMeldungsIdThenGrundstueck(a: AlteGrundbuchmeldungListEntry, b: AlteGrundbuchmeldungListEntry) {
    if (a && a.geschaeftsnummer && b && b.geschaeftsnummer) {
      if (a.geschaeftsnummer.amtNummer === b.geschaeftsnummer.amtNummer) {
        if (a.geschaeftsnummer.jahr === b.geschaeftsnummer.jahr) {
          if (a.geschaeftsnummer.nummer === b.geschaeftsnummer.nummer) {
            if (a.geschaeftsnummer.index === b.geschaeftsnummer.index) {
              return this.sortByMeldungsId(a, b);
            } else {
              return this.comparatorService.orderByComparator(a.geschaeftsnummer.index, b.geschaeftsnummer.index);
            }
          } else {
            return this.comparatorService.orderByComparator(a.geschaeftsnummer.nummer, b.geschaeftsnummer.nummer);
          }
        } else {
          return this.comparatorService.orderByComparator(a.geschaeftsnummer.jahr, b.geschaeftsnummer.jahr);
        }
      } else {
        return this.comparatorService.orderByComparator(a.geschaeftsnummer.amtNummer, b.geschaeftsnummer.amtNummer);
      }
    }
  }

  private sortByMeldungsId(a: AlteGrundbuchmeldungListEntry, b: AlteGrundbuchmeldungListEntry) {
    if (a && a.meldungsId && b && b.meldungsId) {
      if (a.meldungsId === b.meldungsId) {
        return this.sortByGrundstueckNummer(a, b);
      } else {
        return this.comparatorService.orderByComparator(a.meldungsId, b.meldungsId);
      }
    }
  }

  private sortByGrundstueckNummer(a: AlteGrundbuchmeldungListEntry, b: AlteGrundbuchmeldungListEntry) {
    return this.comparatorService.grundstueckComparator(
      {
        grundstueckNummer: a.grundstueck,
        id: { id: undefined }
      },
      {
        grundstueckNummer: b.grundstueck,
        id: { id: undefined }
      }
    );
  }
}

class AlteGrundbuchmeldungListEntryVm {
  public object: AlteGrundbuchmeldungListEntry;
  public dokulinkItem: DokulinkItem;

  constructor(object: AlteGrundbuchmeldungListEntry, dokuLinkBuilderFactory: DokulinkItemBuilderFactory) {
    this.object = object;
    this.dokulinkItem = dokuLinkBuilderFactory.create('ALTE_GRUNDBUCHMELDUNG').setEntry(object).build();
  }
}
